/**
 * Animation utilities for ArbiWin UI components
 * Provides consistent animations and transitions
 */

/**
 * Animation configuration constants
 */
export const ANIMATION_CONFIG = {
  duration: {
    fast: 150,
    medium: 300,
    slow: 500
  },
  easing: {
    ease: 'ease',
    easeIn: 'ease-in',
    easeOut: 'ease-out',
    easeInOut: 'ease-in-out',
    linear: 'linear'
  },
  spring: {
    gentle: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    wobbly: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    stiff: 'cubic-bezier(0.35, 0, 0.25, 1)'
  }
};

/**
 * Animation presets for common use cases
 */
export const ANIMATION_PRESETS = {
  fadeIn: {
    from: { opacity: 0 },
    to: { opacity: 1 },
    duration: ANIMATION_CONFIG.duration.medium,
    easing: ANIMATION_CONFIG.easing.easeOut
  },

  fadeOut: {
    from: { opacity: 1 },
    to: { opacity: 0 },
    duration: ANIMATION_CONFIG.duration.fast,
    easing: ANIMATION_CONFIG.easing.easeIn
  },

  slideInUp: {
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
    duration: ANIMATION_CONFIG.duration.medium,
    easing: ANIMATION_CONFIG.spring.gentle
  },

  slideInDown: {
    from: { opacity: 0, transform: 'translateY(-20px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
    duration: ANIMATION_CONFIG.duration.medium,
    easing: ANIMATION_CONFIG.spring.gentle
  },

  slideInLeft: {
    from: { opacity: 0, transform: 'translateX(-20px)' },
    to: { opacity: 1, transform: 'translateX(0)' },
    duration: ANIMATION_CONFIG.duration.medium,
    easing: ANIMATION_CONFIG.spring.gentle
  },

  slideInRight: {
    from: { opacity: 0, transform: 'translateX(20px)' },
    to: { opacity: 1, transform: 'translateX(0)' },
    duration: ANIMATION_CONFIG.duration.medium,
    easing: ANIMATION_CONFIG.spring.gentle
  },

  scaleIn: {
    from: { opacity: 0, transform: 'scale(0.9)' },
    to: { opacity: 1, transform: 'scale(1)' },
    duration: ANIMATION_CONFIG.duration.medium,
    easing: ANIMATION_CONFIG.spring.gentle
  },

  scaleOut: {
    from: { opacity: 1, transform: 'scale(1)' },
    to: { opacity: 0, transform: 'scale(0.9)' },
    duration: ANIMATION_CONFIG.duration.fast,
    easing: ANIMATION_CONFIG.easing.easeIn
  },

  bounce: {
    keyframes: [
      { transform: 'translateY(0)' },
      { transform: 'translateY(-10px)' },
      { transform: 'translateY(0)' }
    ],
    duration: 600,
    easing: ANIMATION_CONFIG.easing.easeInOut
  },

  shake: {
    keyframes: [
      { transform: 'translateX(0)' },
      { transform: 'translateX(-5px)' },
      { transform: 'translateX(5px)' },
      { transform: 'translateX(-5px)' },
      { transform: 'translateX(0)' }
    ],
    duration: 400,
    easing: ANIMATION_CONFIG.easing.easeInOut
  },

  pulse: {
    keyframes: [
      { opacity: 1, transform: 'scale(1)' },
      { opacity: 0.7, transform: 'scale(1.05)' },
      { opacity: 1, transform: 'scale(1)' }
    ],
    duration: 1000,
    easing: ANIMATION_CONFIG.easing.easeInOut,
    iterationCount: 'infinite'
  }
};

/**
 * Create a Web Animations API animation
 *
 * @param {Element} element - Target element
 * @param {Object} preset - Animation preset or custom keyframes
 * @param {Object} options - Animation options
 * @returns {Animation} Web Animation object
 */
export const createAnimation = function(element, preset, options = {}) {
  if (!element || typeof element.animate !== 'function') {
    console.warn('Element does not support Web Animations API');
    return null;
  }

  const config = { ...preset, ...options };

  let keyframes;
  if (config.keyframes) {
    keyframes = config.keyframes;
  } else if (config.from && config.to) {
    keyframes = [config.from, config.to];
  } else {
    console.warn('Invalid animation configuration');
    return null;
  }

  const animationOptions = {
    duration: config.duration || ANIMATION_CONFIG.duration.medium,
    easing: config.easing || ANIMATION_CONFIG.easing.easeOut,
    fill: config.fill || 'both',
    iterations: config.iterationCount || 1
  };

  try {
    return element.animate(keyframes, animationOptions);
  } catch (error) {
    console.warn('Failed to create animation:', error);
    return null;
  }
};

/**
 * Animate an element with a preset
 *
 * @param {Element} element - Target element
 * @param {string} presetName - Name of animation preset
 * @param {Object} options - Animation options
 * @returns {Promise} Promise that resolves when animation completes
 */
export const animateElement = function(element, presetName, options = {}) {
  return new Promise(function(resolve, reject) {
    const preset = ANIMATION_PRESETS[presetName];
    if (!preset) {
      reject(new Error(`Animation preset "${presetName}" not found`));
      return;
    }

    const animation = createAnimation(element, preset, options);
    if (!animation) {
      reject(new Error('Failed to create animation'));
      return;
    }

    animation.addEventListener('finish', function() {
      resolve(animation);
    });

    animation.addEventListener('cancel', function() {
      reject(new Error('Animation was cancelled'));
    });
  });
};

/**
 * Stagger animations across multiple elements
 *
 * @param {NodeList|Array} elements - Elements to animate
 * @param {string} presetName - Animation preset name
 * @param {Object} options - Animation options
 * @param {number} options.stagger - Delay between animations in ms
 * @returns {Promise} Promise that resolves when all animations complete
 */
export const staggerAnimation = function(elements, presetName, options = {}) {
  const { stagger = 100, ...animationOptions } = options;
  const promises = [];

  Array.from(elements).forEach(function(element, index) {
    const delay = index * stagger;
    const promise = new Promise(function(resolve) {
      setTimeout(function() {
        animateElement(element, presetName, animationOptions)
          .then(resolve)
          .catch(resolve); // Don't fail the entire sequence on individual failures
      }, delay);
    });
    promises.push(promise);
  });

  return Promise.all(promises);
};

/**
 * Create a sequence of animations
 *
 * @param {Array} sequence - Array of animation steps
 * @returns {Promise} Promise that resolves when sequence completes
 */
export const animateSequence = function(sequence) {
  return sequence.reduce(function(promise, step) {
    return promise.then(function() {
      const { element, preset, options = {} } = step;
      return animateElement(element, preset, options);
    });
  }, Promise.resolve());
};

/**
 * Transition utilities for smooth state changes
 */
export const transitionUtils = {
  /**
   * Smooth height transition
   */
  height: function(element, targetHeight, options = {}) {
    const currentHeight = element.offsetHeight;

    return createAnimation(element, {
      from: { height: currentHeight + 'px' },
      to: { height: targetHeight + 'px' }
    }, {
      duration: options.duration || ANIMATION_CONFIG.duration.medium,
      easing: options.easing || ANIMATION_CONFIG.spring.gentle,
      ...options
    });
  },

  /**
   * Smooth width transition
   */
  width: function(element, targetWidth, options = {}) {
    const currentWidth = element.offsetWidth;

    return createAnimation(element, {
      from: { width: currentWidth + 'px' },
      to: { width: targetWidth + 'px' }
    }, {
      duration: options.duration || ANIMATION_CONFIG.duration.medium,
      easing: options.easing || ANIMATION_CONFIG.spring.gentle,
      ...options
    });
  },

  /**
   * Smooth opacity transition
   */
  opacity: function(element, targetOpacity, options = {}) {
    const currentOpacity = window.getComputedStyle(element).opacity;

    return createAnimation(element, {
      from: { opacity: currentOpacity },
      to: { opacity: targetOpacity }
    }, {
      duration: options.duration || ANIMATION_CONFIG.duration.medium,
      easing: options.easing || ANIMATION_CONFIG.easing.easeInOut,
      ...options
    });
  }
};

/**
 * Check if animations are supported and enabled
 *
 * @returns {boolean} Whether animations should be used
 */
export const shouldAnimate = function() {
  // Check for reduced motion preference
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return false;
  }

  // Check for Web Animations API support
  if (typeof Element === 'undefined' || !Element.prototype.animate) {
    return false;
  }

  return true;
};

/**
 * Safe animation wrapper that respects user preferences
 *
 * @param {Function} animationFn - Animation function to execute
 * @param {Function} fallbackFn - Fallback function for no animation
 * @returns {*} Result of animation or fallback function
 */
export const safeAnimate = function(animationFn, fallbackFn) {
  if (shouldAnimate()) {
    try {
      return animationFn();
    } catch (error) {
      console.warn('Animation failed, using fallback:', error);
      return fallbackFn ? fallbackFn() : null;
    }
  }

  return fallbackFn ? fallbackFn() : null;
};

/**
 * Performance monitoring for animations
 */
export const animationPerformance = {
  timings: new Map(),

  start: function(id) {
    this.timings.set(id, performance.now());
  },

  end: function(id) {
    const startTime = this.timings.get(id);
    if (startTime) {
      const duration = performance.now() - startTime;
      this.timings.delete(id);
      return duration;
    }
    return null;
  },

  measure: function(id, animationFn) {
    this.start(id);
    const result = animationFn();

    if (result && result.then) {
      // Handle promises
      return result.finally(() => {
        const duration = this.end(id);
        if (duration > 16) { // More than one frame
          console.warn(`Animation "${id}" took ${duration.toFixed(2)}ms`);
        }
      });
    } else {
      const duration = this.end(id);
      if (duration > 16) {
        console.warn(`Animation "${id}" took ${duration.toFixed(2)}ms`);
      }
      return result;
    }
  }
};

/**
 * Default export with all utilities
 */
export default {
  ANIMATION_CONFIG,
  ANIMATION_PRESETS,
  createAnimation,
  animateElement,
  staggerAnimation,
  animateSequence,
  transitionUtils,
  shouldAnimate,
  safeAnimate,
  animationPerformance
};
